import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Shield, Plus, Edit, Trash2, Users, UserPlus, UserMinus, Lock, KeyRound, Check } from 'lucide-react'
import { motion } from 'framer-motion'
import { api } from '@/lib/api-client'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/use-toast'
import { extractItems } from '@/lib/response-utils'
import type { Role, ActionGroups } from '@/features/roles/api'
import type { User } from '@/features/auth/types'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'

export function SettingsRolesPage() {
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [editingRole, setEditingRole] = useState<Role | null>(null)
    const [roleToDelete, setRoleToDelete] = useState<Role | null>(null)
    const [viewingRoleUsers, setViewingRoleUsers] = useState<Role | null>(null)
    const [managingRole, setManagingRole] = useState<Role | null>(null)
    const [editingPolicy, setEditingPolicy] = useState<Role | null>(null)
    const [viewingPolicy, setViewingPolicy] = useState<Role | null>(null)
    const [selectedActions, setSelectedActions] = useState<Set<string>>(new Set())
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const listState = useListState({ defaultPerPage: 6 })

    // ── Queries ──────────────────────────────────────────────────────────
    const { data: rolesResponse, isLoading } = useQuery({
        queryKey: ['roles'],
        queryFn: () => api.roles.list(),
    })

    const roles: Role[] = useMemo(() => rolesResponse?.data?.roles || [], [rolesResponse])

    const { data: roleUsersResponse, isLoading: isLoadingRoleUsers } = useQuery({
        queryKey: ['roles', viewingRoleUsers?.id || managingRole?.id, 'users'],
        queryFn: () => api.roles.listUsers((viewingRoleUsers || managingRole)!.id),
        enabled: !!(viewingRoleUsers || managingRole),
    })

    const roleUsers: User[] = roleUsersResponse?.data?.users || []

    const { data: allUsersResponse } = useQuery({
        queryKey: ['admin', 'users'],
        queryFn: () => api.users.list(),
        enabled: !!managingRole,
    })

    const allUsers = extractItems<User>(allUsersResponse)

    const { data: actionsResponse } = useQuery({
        queryKey: ['roles', 'actions'],
        queryFn: () => api.roles.getActions(),
        enabled: !!(editingPolicy || viewingPolicy),
    })

    const actionGroups: ActionGroups = actionsResponse?.data?.groups || {}

    // Initialize selectedActions when opening the policy editor/viewer
    const activePolicyRole = editingPolicy || viewingPolicy
    const isPolicyReadonly = !!viewingPolicy
    useEffect(() => {
        if (activePolicyRole) {
            setSelectedActions(new Set(activePolicyRole.allowed_actions || []))
        }
    }, [activePolicyRole])

    // ── Mutations ────────────────────────────────────────────────────────
    const createMutation = useMutation({
        mutationFn: (data: { name: string; description?: string }) => api.roles.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            setIsCreateOpen(false)
            toast({ title: 'Role created', description: 'New role has been created.' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.error || 'Failed to create role',
                variant: 'destructive',
            })
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string } }) =>
            api.roles.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            setEditingRole(null)
            toast({ title: 'Role updated' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.error || 'Failed to update role',
                variant: 'destructive',
            })
        },
    })

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.roles.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            setRoleToDelete(null)
            toast({ title: 'Role deleted' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.error || 'Failed to delete role',
                variant: 'destructive',
            })
        },
    })

    const policyMutation = useMutation({
        mutationFn: ({ id, actions }: { id: string; actions: string[] }) =>
            api.roles.updatePolicy(id, actions),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            setEditingPolicy(null)
            toast({ title: 'Policy updated', description: 'Role permissions have been saved.' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.error || 'Failed to update policy',
                variant: 'destructive',
            })
        },
    })

    const addUsersMutation = useMutation({
        mutationFn: ({ roleId, userIds }: { roleId: string; userIds: string[] }) =>
            api.roles.addUsers(roleId, userIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            queryClient.invalidateQueries({ queryKey: ['roles', managingRole?.id, 'users'] })
            toast({ title: 'User(s) added to role' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.error || 'Failed to add users',
                variant: 'destructive',
            })
        },
    })

    const removeUserMutation = useMutation({
        mutationFn: ({ roleId, userId }: { roleId: string; userId: string }) =>
            api.roles.removeUser(roleId, userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            queryClient.invalidateQueries({ queryKey: ['roles', (viewingRoleUsers || managingRole)?.id, 'users'] })
            toast({ title: 'User removed from role' })
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.response?.data?.error || 'Failed to remove user',
                variant: 'destructive',
            })
        },
    })

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        createMutation.mutate({
            name: formData.get('name') as string,
            description: formData.get('description') as string,
        })
    }

    const handleUpdate = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!editingRole) return
        const formData = new FormData(e.currentTarget)
        updateMutation.mutate({
            id: editingRole.id,
            data: {
                name: formData.get('name') as string,
                description: formData.get('description') as string,
            },
        })
    }

    const handleSavePolicy = () => {
        if (!editingPolicy) return
        policyMutation.mutate({
            id: editingPolicy.id,
            actions: Array.from(selectedActions),
        })
    }

    const toggleAction = (action: string) => {
        setSelectedActions(prev => {
            const next = new Set(prev)
            if (next.has(action)) {
                next.delete(action)
            } else {
                next.add(action)
            }
            return next
        })
    }

    const toggleGroup = (groupActions: string[]) => {
        setSelectedActions(prev => {
            const next = new Set(prev)
            const allSelected = groupActions.every(a => next.has(a))
            if (allSelected) {
                groupActions.forEach(a => next.delete(a))
            } else {
                groupActions.forEach(a => next.add(a))
            }
            return next
        })
    }

    const selectAll = () => {
        const all = Object.values(actionGroups).flat()
        setSelectedActions(new Set(all))
    }

    const selectNone = () => {
        setSelectedActions(new Set())
    }

    const usersNotInRole = allUsers.filter(
        u => !roleUsers.some(ru => ru.id === u.id)
    )

    // ── Filtering + Pagination ────────────────────────────────────────────
    const filteredRoles = useMemo(() => {
        const term = listState.search.toLowerCase()
        if (!term) return roles
        return roles.filter(r =>
            r.name.toLowerCase().includes(term) ||
            r.description?.toLowerCase().includes(term)
        )
    }, [roles, listState.search])

    const totalPages = Math.max(1, Math.ceil(filteredRoles.length / listState.perPage))
    const paginatedRoles = useMemo(() => {
        const start = (listState.page - 1) * listState.perPage
        return filteredRoles.slice(start, start + listState.perPage)
    }, [filteredRoles, listState.page, listState.perPage])

    // ── Loading ──────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        )
    }

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
        >
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5" />
                            All Roles
                        </CardTitle>
                        <CardDescription>
                            Manage roles, permissions, and user assignments
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <ImportExportButtons
                            entityType="roles"
                            entityLabel="Roles"
                        />
                        <AuthorizationGate actions={['update_policy']}>
                            <Button onClick={() => setIsCreateOpen(true)}>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Role
                            </Button>
                        </AuthorizationGate>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Search Toolbar */}
                    <DataToolbar
                        listState={listState}
                        searchPlaceholder="Search roles..."
                        totalItems={filteredRoles.length}
                    />

                    {/* ── Roles List ──────────────────────────────────────────── */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
                        {paginatedRoles.map((role) => (
                            <motion.div
                                key={role.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Card>
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-lg flex items-center gap-2">
                                                <Shield className="h-4 w-4 text-blue-500" />
                                                {role.name}
                                                {role.is_system && (
                                                    <Badge variant="secondary" className="text-xs font-normal gap-1">
                                                        <Lock className="h-3 w-3" />
                                                        Built-in
                                                    </Badge>
                                                )}
                                            </CardTitle>
                                            {!role.is_system && (
                                                <AuthorizationGate actions={['update_policy']}>
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setEditingRole(role)}
                                                        >
                                                            <Edit className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700"
                                                            onClick={() => setRoleToDelete(role)}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                </AuthorizationGate>
                                            )}
                                        </div>
                                        <CardDescription>{role.description || 'No description'}</CardDescription>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <Badge variant="outline" className="flex items-center gap-1">
                                                    <Users className="h-3 w-3" />
                                                    {role.user_count} user{role.user_count !== 1 ? 's' : ''}
                                                </Badge>
                                                <Badge variant="secondary" className="flex items-center gap-1">
                                                    <KeyRound className="h-3 w-3" />
                                                    {(role.allowed_actions || []).length} action{(role.allowed_actions || []).length !== 1 ? 's' : ''}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {role.is_system ? (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="flex-1"
                                                        onClick={() => setViewingPolicy(role)}
                                                    >
                                                        <KeyRound className="h-3.5 w-3.5 mr-1" />
                                                        View Policy
                                                    </Button>
                                                ) : (
                                                    <AuthorizationGate actions={['update_policy']}>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="flex-1"
                                                            onClick={() => setEditingPolicy(role)}
                                                        >
                                                            <KeyRound className="h-3.5 w-3.5 mr-1" />
                                                            Edit Policy
                                                        </Button>
                                                    </AuthorizationGate>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setViewingRoleUsers(role)}
                                                >
                                                    <Users className="h-3.5 w-3.5 mr-1" />
                                                    View Users
                                                </Button>
                                                {!role.is_system && (
                                                    <AuthorizationGate actions={['update_policy']}>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => setManagingRole(role)}
                                                        >
                                                            <UserPlus className="h-3.5 w-3.5 mr-1" />
                                                            Manage
                                                        </Button>
                                                    </AuthorizationGate>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {filteredRoles.length > 0 && (
                        <div className="mt-6">
                            <DataPagination
                                page={listState.page}
                                totalPages={totalPages}
                                onPageChange={listState.setPage}
                                totalItems={filteredRoles.length}
                                perPage={listState.perPage}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── Create Role Dialog ──────────────────────────────────── */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New Role</DialogTitle>
                        <DialogDescription>Define a new role for the system.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="name">Role Name</Label>
                                <Input id="name" name="name" placeholder="e.g. auditor" required />
                            </div>
                            <div>
                                <Label htmlFor="description">Description</Label>
                                <Input id="description" name="description" placeholder="What this role allows" />
                            </div>
                        </div>
                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={createMutation.isPending}>
                                {createMutation.isPending ? 'Creating...' : 'Create Role'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ── Edit Role Dialog ────────────────────────────────────── */}
            <Dialog open={!!editingRole} onOpenChange={() => setEditingRole(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Role</DialogTitle>
                        <DialogDescription>Update role details.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUpdate}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="edit-name">Role Name</Label>
                                <Input id="edit-name" name="name" defaultValue={editingRole?.name} required />
                            </div>
                            <div>
                                <Label htmlFor="edit-description">Description</Label>
                                <Input id="edit-description" name="description" defaultValue={editingRole?.description} />
                            </div>
                        </div>
                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={() => setEditingRole(null)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={updateMutation.isPending}>
                                {updateMutation.isPending ? 'Updating...' : 'Update Role'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ── Delete Role Dialog ──────────────────────────────────── */}
            <Dialog open={!!roleToDelete} onOpenChange={() => setRoleToDelete(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Role</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete <strong>{roleToDelete?.name}</strong>?
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRoleToDelete(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={() => roleToDelete && deleteMutation.mutate(roleToDelete.id)}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? 'Deleting...' : 'Delete Role'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Edit / View Policy Dialog ────────────────────────────── */}
            <Dialog open={!!activePolicyRole} onOpenChange={() => { setEditingPolicy(null); setViewingPolicy(null) }}>
                <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="h-5 w-5" />
                            {isPolicyReadonly ? 'View Policy' : 'Edit Policy'} — {activePolicyRole?.name}
                        </DialogTitle>
                        <DialogDescription>
                            {isPolicyReadonly
                                ? 'This is a built-in system role. Its policy cannot be modified.'
                                : 'Select which actions this role is allowed to perform.'}
                            {' '}
                            <span className="font-medium text-blue-600">
                                {selectedActions.size} action{selectedActions.size !== 1 ? 's' : ''} selected
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    {/* Bulk controls — hidden in readonly mode */}
                    {!isPolicyReadonly && (
                        <div className="flex items-center gap-2 pb-2 border-b">
                            <Button type="button" variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                            <Button type="button" variant="outline" size="sm" onClick={selectNone}>Clear All</Button>
                        </div>
                    )}

                    {/* Grouped actions */}
                    <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1">
                        {Object.entries(actionGroups).map(([group, actions]) => {
                            const allChecked = actions.every(a => selectedActions.has(a))
                            const someChecked = actions.some(a => selectedActions.has(a))

                            return (
                                <div key={group} className="border rounded-lg p-3">
                                    <div
                                        className={`flex items-center gap-2 mb-2 ${isPolicyReadonly ? '' : 'cursor-pointer'}`}
                                        onClick={isPolicyReadonly ? undefined : () => toggleGroup(actions)}
                                    >
                                        <div className={`h-4 w-4 rounded border flex items-center justify-center text-xs ${allChecked
                                            ? 'bg-blue-500 border-blue-500 text-white'
                                            : someChecked
                                                ? 'bg-blue-100 border-blue-300 text-blue-500'
                                                : 'border-gray-300'
                                            }`}>
                                            {allChecked && <Check className="h-3 w-3" />}
                                            {someChecked && !allChecked && <span>–</span>}
                                        </div>
                                        <span className="font-medium text-sm">{group}</span>
                                        <span className="text-xs text-gray-400 ml-auto">
                                            {actions.filter(a => selectedActions.has(a)).length}/{actions.length}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 pl-6">
                                        {actions.map(action => (
                                            <label
                                                key={action}
                                                className={`flex items-center gap-2 py-1 px-2 rounded text-sm ${isPolicyReadonly ? 'opacity-75' : 'hover:bg-gray-50 cursor-pointer'
                                                    }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedActions.has(action)}
                                                    onChange={isPolicyReadonly ? undefined : () => toggleAction(action)}
                                                    disabled={isPolicyReadonly}
                                                    className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                                                />
                                                <span className="text-gray-700 font-mono text-xs">
                                                    {action}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <DialogFooter className="pt-2 border-t">
                        {isPolicyReadonly ? (
                            <Button type="button" variant="outline" onClick={() => setViewingPolicy(null)}>
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button type="button" variant="outline" onClick={() => setEditingPolicy(null)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSavePolicy} disabled={policyMutation.isPending}>
                                    {policyMutation.isPending ? 'Saving...' : 'Save Policy'}
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── View Users Dialog ───────────────────────────────────── */}
            <Dialog open={!!viewingRoleUsers} onOpenChange={() => setViewingRoleUsers(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Users in "{viewingRoleUsers?.name}"
                        </DialogTitle>
                        <DialogDescription>
                            {roleUsers.length} user{roleUsers.length !== 1 ? 's' : ''} assigned to this role
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-80 overflow-y-auto space-y-2">
                        {isLoadingRoleUsers ? (
                            <div className="flex justify-center py-8"><LoadingSpinner /></div>
                        ) : roleUsers.length > 0 ? (
                            roleUsers.map(user => (
                                <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                                            <span className="text-xs font-medium text-white">
                                                {user.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <div className="font-medium text-sm">{user.name}</div>
                                            <div className="text-xs text-gray-500">{user.email}</div>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700"
                                        onClick={() => {
                                            removeUserMutation.mutate({
                                                roleId: viewingRoleUsers!.id,
                                                userId: user.id,
                                            })
                                        }}
                                    >
                                        <UserMinus className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-gray-400 py-8 text-sm">No users assigned</p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Manage Users Dialog ─────────────────────────────────── */}
            <Dialog open={!!managingRole} onOpenChange={() => setManagingRole(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="h-5 w-5" />
                            Manage Users — {managingRole?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Add or remove users from this role
                        </DialogDescription>
                    </DialogHeader>

                    {/* Current role members */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Current Members ({roleUsers.length})</h4>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                            {roleUsers.map(user => (
                                <div key={user.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                                            {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="text-sm">{user.name}</span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700 h-7 px-2"
                                        onClick={() => removeUserMutation.mutate({
                                            roleId: managingRole!.id,
                                            userId: user.id,
                                        })}
                                    >
                                        <UserMinus className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ))}
                            {roleUsers.length === 0 && (
                                <p className="text-center text-xs text-gray-400 py-3">No users assigned yet</p>
                            )}
                        </div>
                    </div>

                    {/* Add users */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Add Users</h4>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                            {usersNotInRole.map(user => (
                                <div key={user.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-medium">
                                            {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <span className="text-sm">{user.name}</span>
                                            <span className="text-xs text-gray-400 ml-2">{user.email}</span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-green-600 hover:text-green-700 h-7 px-2"
                                        onClick={() => addUsersMutation.mutate({
                                            roleId: managingRole!.id,
                                            userIds: [user.id],
                                        })}
                                    >
                                        <UserPlus className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ))}
                            {usersNotInRole.length === 0 && (
                                <p className="text-center text-xs text-gray-400 py-3">All users are already in this role</p>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </motion.div>
    )
}
